<template>
  <div v-if="resume" class="editor">
    <transition name="fade">
      <Notification
        v-if="notification.message"
        class="notification"
        :html-message="notification.message"
        :is-success="notification.success"
      />
    </transition>
    <LeftPanel
      v-if="currentMode === 'editor' || currentMode === 'preview'"
      :disabled="currentMode === 'preview'"
      :scroll-to="scrollTo"
      :categories="categories.filter(c => !c.disabled)"
      :selected-item-id="currentItemId"
      :select-category="selectCategory"
      :selected-category-id="currentCategoryId"
      :items="items"
      :resume="resume"
      class="left panel"
      :pages="pages"
      @item-selected="itemSelected"
      @category-selected="categorySelected"
      @add-item="addItem"
      @delete-item="deleteItem"
    />
    <LeftPanelDesign
      v-if="currentMode === 'design'"
      :resume="resume"
      :design-category="designCategory"
      class="left panel"
      @design-category-selected="designCategorySelected"
    />
    <transition name="slide">
      <component
        :is="rightPanel"
        v-if="
          currentMode === 'editor' && ['candidateInfo', 'conviction', 'skills', 'contact'].includes(currentCategoryId)
        "
        :scroll-to="scrollTo"
        :resume="resume"
        :items="items.filter(i => i.type === currentCategoryId)"
        :sub-items="subItems"
        :title="getSectionTitle(currentCategoryId)"
        class="right panel"
        :current-section="currentSection"
        @section-selected="section => (currentSection = section)"
        @add-item="addItem"
        @delete-item="deleteItem"
      />
      <component
        :is="rightPanel"
        v-else-if="currentItemId && currentMode === 'editor'"
        :key="currentItemId"
        :resume="resume"
        :item="items.find(i => i.id === currentItemId)"
        :sub-items="subItems"
        :title="getSectionTitle(currentCategoryId)"
        class="right panel"
        :current-section="currentSection"
        @section-selected="section => (currentSection = section)"
        @add-item="addItem"
        @delete-item="deleteItem"
      />
    </transition>
    <transition name="slide">
      <TemplatePanel
        v-if="currentMode === 'design' && designCategory === 'template'"
        :resume="resume"
        class="right panel"
      />
    </transition>
    <transition name="slide">
      <ColorPalettePanel
        v-if="currentMode === 'design' && designCategory === 'colorPalette'"
        :resume="resume"
        class="right panel"
      />
    </transition>
    <div id="resumeRenderer" ref="resumeContainer" class="pages">
      <div v-if="resume" ref="scaler" class="scaler" :style="{ transform: `scale(${scale})` }">
        <ResumeRenderer
          v-if="currentResume"
          :ref="'resumeRenderer'"
          :resume="resume"
          :items="items"
          :current-item-id="currentItemId"
          :current-section="currentSection"
          :current-category-id="currentCategoryId"
          @item-selected="itemSelected"
          @category-selected="categorySelected"
          @pages="page => (pages = page)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import LeftPanel from '@/components/panels/LeftPanelEditor';
import ExperiencePanel from '@/components/panels/Experience';
import FormationPanel from '@/components/panels/Formation';
import ProjectPanel from '@/components/panels/Project';
import ConvictionPanel from '@/components/panels/Conviction';
import SkillsPanel from '@/components/panels/Skills';
import CandidatePanel from '@/components/panels/Candidate';
import TemplatePanel from '@/components/panels/Template';
import ColorPalettePanel from '@/components/panels/ColorPalette';
import ContactPanel from '@/components/panels/Contact';
import ResumeRenderer from '@/components/ResumeRenderer';
import LeftPanelDesign from '@/components/panels/LeftPanelDesign';
import Notification from '@/components/Notification';
import { mapGetters } from 'vuex';
import PenIcon from '@/assets/icons/pen.svg';
import PaintIcon from '@/assets/icons/paint.svg';
import EyeIcon from '@/assets/icons/eye.svg';
import ExportIcon from '@/assets/icons/arrow_diagonal.svg';
import BackIcon from '@/assets/icons/arrow_back.svg';

const defaultSettings = {
  sections: [
    { id: 'candidateInfo', disabled: false },
    { id: 'conviction', disabled: false },
    { id: 'skills', disabled: false },
    { id: 'formations', disabled: false },
    { id: 'projects', disabled: false },
    { id: 'experiences', disabled: false },
    { id: 'contact', loginRequired: true, disabled: false },
  ],
};
export default {
  name: 'EditorView',
  components: {
    LeftPanel,
    ExperiencePanel,
    ConvictionPanel,
    SkillsPanel,
    FormationPanel,
    ProjectPanel,
    ContactPanel,
    TemplatePanel,
    ColorPalettePanel,
    CandidatePanel,
    ResumeRenderer,
    LeftPanelDesign,
    PenIcon,
    PaintIcon,
    EyeIcon,
    ExportIcon,
    BackIcon,
    Notification,
  },
  data() {
    return {
      categories: [],
      designCategory: 'template',
      currentItemId: '',
      currentHoveredItemId: '',
      currentCategoryId: '',
      currentSection: '',
      downloading: false,
      pages: [],
      scale: undefined,
    };
  },

  computed: {
    ...mapGetters(['currentResume', 'user', 'loading', 'currentMode', 'leftPannelWidth', 'notification']),
    subItems() {
      if (!this.currentItemId) return [];
      return this.items.filter(i => i.parentId === this.currentItemId);
    },
    resume() {
      return this.currentResume;
    },
    items() {
      if (this.currentResume) return this.currentResume.items;
      return [];
    },
    titles() {
      const titles = this.items.find(i => i.type === 'titles');
      return titles ? titles.content : undefined;
    },
    currentCategory() {
      const category = this.categories.find(c => c.id === this.currentCategoryId);
      return category || {};
    },
    rightPanel() {
      if (!this.currentCategoryId) return null;
      const category = this.categories.find(c => c.id === this.currentCategoryId);
      if (category && category.panel) {
        return category.panel;
      }
      console.error(`Missing configuration panel for category ${this.currentCategoryId}`);
      return null;
    },
    resumeId() {
      return this.$route.params.id;
    },
    canEdit() {
      if (!this.resume) return false;
      return this.resume.permissions === 0 || this.resume.permissions === 2;
    },
    resumeLanguage() {
      return this.resume?.language || 'fr';
    },
  },

  watch: {
    leftPannelWidth() {
      this.recenterResume();
    },
    currentMode() {
      this.recenterResume();
    },
    currentItemId() {
      this.recenterResume();
    },
    currentCategoryId() {
      this.recenterResume();
    },
    designCategory() {
      this.recenterResume();
    },
    $route() {
      this.reloadResume();
    },
    'resume.template.settings': {
      handler(settings) {
        this.updateSettings(defaultSettings);
        if (settings) {
          this.updateSettings(settings);
        }
      },
      deep: true,
    },
  },

  created() {
    this.setCategories();
  },

  async mounted() {
    await this.reloadResume();
    if (!this.user) {
      window.onbeforeunload = () =>
        confirm(
          "ATTENTION : Vous vous appretez à quitter Washi, pour garder votre dossier technique et y accéder à nouveau, veuillez copier et stocker l'URL de cette page. Vous en avez assez de stocker des URLs pour éditer vos dossiers ? Inscrivez-vous à Washi et accéder à un dashboard de tous vos dossier techniques"
        );
    } else window.onbeforeunload = () => null;

    await this.$store.dispatch('items.resetUndoRedo');
    document.addEventListener('keydown', this.keyListenerForUndoRedo);
    this.calculateResumeScale();
    window.addEventListener('resize', this.calculateResumeScale);
  },
  beforeUnmount() {
    document.removeEventListener('keydown', this.keyListenerForUndoRedo);
    window.onbeforeunload = () => null;
    window.removeEventListener('resize', this.calculateResumeScale);
  },

  methods: {
    setCategories() {
      this.categories = [
        {
          id: 'candidateInfo',
          name: this.$t(`resume.sections.${this.resumeLanguage}.candidateInfo`),
          panel: 'CandidatePanel',
          canAddItem: false,
        },
        {
          id: 'conviction',
          name: this.$t(`resume.sections.${this.resumeLanguage}.conviction`),
          panel: 'ConvictionPanel',
          canAddItem: false,
        },
        {
          id: 'skills',
          name: this.$t(`resume.sections.${this.resumeLanguage}.skills`),
          panel: 'SkillsPanel',
          canAddItem: true,
        },
        {
          id: 'formations',
          name: this.$t(`resume.sections.${this.resumeLanguage}.formations`),
          panel: 'FormationPanel',
          canAddItem: true,
        },
        {
          id: 'projects',
          name: this.$t(`resume.sections.${this.resumeLanguage}.projects`),
          panel: 'ProjectPanel',
          canAddItem: true,
        },
        {
          id: 'experiences',
          name: this.$t(`resume.sections.${this.resumeLanguage}.experiences`),
          panel: 'ExperiencePanel',
          canAddItem: true,
        },
        {
          id: 'contact',
          name: this.$t(`resume.sections.${this.resumeLanguage}.contact`),
          panel: 'ContactPanel',
          canAddItem: false,
          loginRequired: true,
        },
      ];
    },
    updateSettings(settings) {
      if (settings.sections) {
        settings.sections.forEach(section => {
          const category = this.categories.find(category => category.id === section.id);
          if (category) {
            Object.assign(category, section);
          }
        });
      }
    },
    getSectionTitle(categorieId) {
      if (this.titles && this.titles[categorieId]) {
        return this.titles[categorieId];
      }

      return this.$t(`resume.sections.${this.resumeLanguage}.${categorieId}`);
    },
    async reloadResume() {
      await this.$store.dispatch('resumes.getCurrent', this.$route.params.id);
      this.$store.dispatch('resumes.setMode', {
        currentMode: this.canEdit ? 'editor' : 'preview',
      });
      if (!this.user)
        window.onbeforeunload = () =>
          confirm(
            "ATTENTION : Vous vous appretez à quitter Washi, pour garder votre dossier technique et y accéder à nouveau, veuillez copier et stocker l'URL de cette page. Vous en avez assez de stocker des URLs pour éditer vos dossiers ? Inscrivez-vous à Washi et accéder à un dashboard de tous vos dossier techniques"
          );
      else window.onbeforeunload = () => null;
      this.recenterResume();
      this.updateSettings(defaultSettings);
      this.updateSettings(this.currentResume.template.settings);
    },
    recenterResume() {
      let padding = this.currentCategoryId || this.currentItemId ? this.leftPannelWidth : 0;
      let space = this.$refs.resumeContainer
        ? this.$refs.resumeContainer.offsetWidth - this.$refs.resumeRenderer?.$el.getBoundingClientRect().width
        : 0;

      padding += (space - padding) / 2;
      padding = Math.max(0, Math.min(padding, space - 32));
      if (this.currentMode === 'design') {
        padding = 340;
      }

      if (this.$refs.resumeContainer) this.$refs.resumeContainer.style.paddingLeft = `${padding}px`;
    },
    scrollTo(id) {
      this.$refs['resumeRenderer'].scrollTo(id);
    },
    selectCategory(categoryId) {
      if (this.currentCategoryId === categoryId) {
        this.currentCategoryId = '';
      } else {
        if (!this.categories.find(c => c.id === categoryId).canAddItem || categoryId === 'skills') {
          this.currentCategoryId = categoryId;
        }
      }
    },
    copyLink() {
      navigator.clipboard.writeText(window.location);
    },
    itemSelected(itemId, section) {
      if (this.currentItemId === itemId && (this.currentSection === section || !section)) {
        this.currentItemId = '';
        this.currentSection = '';
        this.currentCategoryId = '';
        return;
      }
      const item = this.items.find(i => i.id === itemId);
      if (section) {
        this.currentSection = section;
      } else {
        this.currentSection = 'info';
      }
      if (!item || !itemId) {
        this.currentItemId = '';
        return;
      }
      if (item && item.parentId) {
        return this.itemSelected(item.parentId, section);
      }
      const categoryId = item.type;
      this.currentItemId = item.id;
      this.currentCategoryId = categoryId;
    },
    categorySelected(categoryId) {
      this.currentCategoryId = categoryId;
      if (!this.currentCategory.canAddItem) {
        const currentItem = this.items.find(i => i.type === categoryId);
        if (currentItem) {
          this.currentItemId = currentItem.id;
        } else {
          this.addItem(categoryId);
        }
      }
    },
    designCategorySelected(category) {
      this.designCategory = category;
    },
    async deleteItem(item) {
      try {
        const result = await this.$store.dispatch('setPopup', {
          popupType: 'ConfirmPopup',
          popupData: {
            title: `SUPPRIMER ${item.content.title ? item.content.title : ''}`,
            content: `Cette action ne peut être annulée et vous perdrez définitivement les données.`,
            company: false,
            confirm: 'SUPPRIMER',
            delete: true,
          },
        });
        if (result) {
          this.currentCategoryId = '';
          this.currentItemId = '';
          return this.$store.dispatch('items.delete', item.id);
        }
      } catch (e) {
        console.error(e);
      }
    },
    async addItem(categoryId) {
      const item = {
        type: categoryId,
        order: Math.max(...this.items.filter(i => i.type === categoryId).map(i => i.order), 0) + 1,
        content: {},
      };
      const newItem = await this.$store.dispatch('items.create', {
        resumeId: this.resume.id,
        item,
      });
      this.$nextTick(() => {
        this.currentItemId = newItem.id;
        this.currentCategoryId = categoryId;
      });
    },
    calculateResumeScale() {
      if (800 / window.innerWidth > 1) {
        this.scale = 1 / (800 / window.innerWidth);
      } else {
        this.scale = undefined;
      }
      this.$nextTick(this.recenterResume);
    },
  },
};
</script>

<style scoped lang="scss">
@use '@/styles/colors.scss';
@use '@/styles/responsive/editor.scss';

.slide-enter-active,
.slide-leave-active {
  transition: all 0.3s ease;
}
.slide-enter,
.slide-leave-to {
  transform: translateX(-320px);
}
.back-button {
  display: flex;
  justify-content: center;
  align-items: center;
  text-transform: uppercase;
  color: white;
  border-radius: 3px;
  padding: 4px 6px;
  background-color: colors.$main-color;
  font-weight: bold;
  font-size: 11px;
  letter-spacing: 1.3px;
  cursor: pointer;
  .icon {
    width: 9px;
    margin-left: 6px;
  }
  &:hover {
    opacity: 0.8;
  }
}

.editor {
  display: flex;
  padding-top: app.$topbar-height;
  justify-content: space-between;
  position: relative;

  .panel {
    width: 320px;
    height: calc(100vh - #{app.$topbar-height});
    background: #f2f2f2;
    box-sizing: border-box;
    flex-shrink: 0;
    overflow: auto;

    ::-webkit-scrollbar {
      width: 10px;
    }

    /* Track */
    ::-webkit-scrollbar-track {
      background: #f1f1f1;
    }

    /* Handle */
    ::-webkit-scrollbar-thumb {
      background: #888;
    }

    /* Handle on hover */
    ::-webkit-scrollbar-thumb:hover {
      background: #555;
    }
    &.left {
      width: 320px;
      z-index: 12;
      background: colors.$lightest-grey-color;
    }

    &.right {
      z-index: 11;
      position: absolute;
      left: 320px;
      background: colors.$light-grey-color;
      padding: 7px 12px 0 24px;
    }
    @media only screen and (max-width: 1370px) {
      &.left {
        width: 260px;
      }
    }
    @media only screen and (max-width: 1370px) {
      &.right {
        width: 260px;
        left: 260px;
      }
    }
  }

  .pages {
    height: calc(100vh - #{app.$topbar-height});
    overflow-y: scroll;
    flex-grow: 1;
    display: flex;
    flex-direction: column;
    transition: 0.3s padding;

    .scaler {
      height: 0; // resumeRenderer height is important not scaler ( usefull to remove bottom margin )
      transform-origin: top left;

      @media only screen and (max-width: 1370px) {
        transform: scale(0.6);
      }
      @media only screen and (max-width: 1000px) {
        transform: scale(0.4);
      }
    }
  }
}

.notification {
  position: fixed;
  top: 64px;
}
</style>
